import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Alert,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { projectService } from '../services/projectService';
import { ProjectForm } from '../components/projects/ProjectForm';
import { useContextStore } from '../store/contextStore';
import { useNotificationStore } from '../store/notificationStore';
import type { ProjectDto } from '../types/project';
import { TextField } from '../@core/components/mui';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import { PageListLayout } from '../components/layouts/PageListLayout';

/**
 * Project list page component.
 */
export const ProjectListPage = () => {
  const {
    currentTenant,
    currentProject,
    setProject,
    loadProjects: refreshSwitcherProjects,
  } = useContextStore();
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [openForm, setOpenForm] = useState(false);
  const [editingProject, setEditingProject] = useState<ProjectDto | null>(null);
  const { showNotification } = useNotificationStore();

  const loadProjects = async () => {
    if (!currentTenant) {
      setError('Please select a tenant first');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await projectService.getProjects(currentTenant.id, {
        pageNumber,
        pageSize,
        searchTerm: searchTerm || undefined,
      });
      setProjects(result.items);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load projects');
      showNotification(err.response?.data?.message || 'Failed to load projects', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, [currentTenant, pageNumber, searchTerm]);

  const handleCreate = () => {
    setEditingProject(null);
    setOpenForm(true);
  };

  const handleEdit = (project: ProjectDto) => {
    setEditingProject(project);
    setOpenForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!currentTenant) return;

    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }

    try {
      await projectService.deleteProject(id, currentTenant.id);
      showNotification('Project deleted successfully', 'success');
      if (currentProject?.id === id) {
        setProject(null);
      }
      loadProjects();
      void refreshSwitcherProjects(currentTenant.id);
    } catch (err: any) {
      showNotification(err.response?.data?.message || 'Failed to delete project', 'error');
    }
  };

  const handleFormClose = () => {
    setOpenForm(false);
    setEditingProject(null);
    loadProjects();
    if (currentTenant) {
      void refreshSwitcherProjects(currentTenant.id);
    }
  };

  // Define table columns
  const tableColumns: DataTableColumn<ProjectDto>[] = [
    {
      id: 'name',
      label: 'Name',
      minWidth: 200,
    },
    {
      id: 'slug',
      label: 'Slug',
      minWidth: 150,
      render: (project) => (
        <Typography variant="body2" color="text.secondary">
          {project.slug}
        </Typography>
      ),
    },
    {
      id: 'description',
      label: 'Description',
      minWidth: 300,
      render: (project) => (
        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 300 }}>
          {project.description || '-'}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      render: (project) => (
        <Chip
          label={project.isActive ? 'Active' : 'Inactive'}
          color={project.isActive ? 'success' : 'error'}
          size="small"
        />
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 120,
      align: 'right',
      render: (project) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEdit(project)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => handleDelete(project.id)} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (!currentTenant) {
    return (
      <Box sx={{ p: 6 }}>
        <Alert severity="info">Please select a tenant first to view projects.</Alert>
      </Box>
    );
  }

  return (
    <>
      <PageListLayout
        title={`Projects - ${currentTenant.name}`}
        createButton={{ label: 'Create Project', onClick: handleCreate, icon: <AddIcon /> }}
        error={error || undefined}
        onErrorDismiss={() => setError(null)}
        loading={loading}
        isEmpty={projects.length === 0}
        emptyState={{
          title: 'No projects found',
          description: 'Create a new project to get started.',
          action: { label: 'Create Project', onClick: handleCreate },
        }}
        filters={
          <TextField
            label="Search projects"
            size="small"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPageNumber(1);
            }}
            sx={{ flexGrow: 1, minWidth: 200 }}
          />
        }
        pagination={{
          page: pageNumber,
          totalPages: Math.ceil(totalCount / pageSize),
          onChange: (page) => setPageNumber(page),
        }}
      >
        <DataTable
          columns={tableColumns}
          rows={projects}
        />
      </PageListLayout>

      {openForm && (
        <ProjectForm
          project={editingProject}
          open={openForm}
          onClose={handleFormClose}
        />
      )}
    </>
  );
};
